import { Globe, Monitor, type LucideIcon } from 'lucide-react';
import type { TeamSession } from './types';

/**
 * "Chrome on Windows", or "NuDent app" for the desktop.
 *
 * Read off the user agent the session was created with. Deliberately coarse:
 * it is there so a person recognises their own laptop, not to fingerprint it.
 */
export function describeDevice(session: Pick<TeamSession, 'portal' | 'userAgent'>): {
  label: string;
  icon: LucideIcon;
} {
  const ua = session.userAgent ?? '';
  if (session.portal === 'desktop') {
    const os = osOf(ua);
    return { label: os ? `NuDent app on ${os}` : 'NuDent app', icon: Monitor };
  }
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\//.test(ua)
      ? 'Opera'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'A browser';
  const os = osOf(ua);
  return { label: os ? `${browser} on ${os}` : browser, icon: Globe };
}

function osOf(ua: string): string | null {
  if (/Windows/.test(ua)) return 'Windows';
  if (/iPhone|iPad/.test(ua)) return 'iOS';
  if (/Mac OS X|Macintosh/.test(ua)) return 'macOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Linux/.test(ua)) return 'Linux';
  return null;
}

/** "Just now", "5 min ago", "3 h ago", "2 days ago", then the date. */
export function since(iso: string, now: number): string {
  const minutes = Math.floor((now - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${String(minutes)} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${String(hours)} h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return days === 1 ? 'yesterday' : `${String(days)} days ago`;
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
